import React, { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Store,
  RefreshCw,
  ArrowLeft,
  Calendar,
  DollarSign,
  FileSpreadsheet,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { DateRangePicker, DateRange } from '@/components/common/DateRangePicker';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useCouponSales,
  useMerchantCategories,
  useMerchants,
  useMerchantMonthlySales,
} from '@/hooks/useVendors';

const ALL_VALUE = 'all';

export function VendorSalesPage() {
  // Pagination State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Detail View State
  const [viewingMerchant, setViewingMerchant] = useState<{ id: number; name: string } | null>(null);
  const [detailMonth, setDetailMonth] = useState<string>(
    new Date().toLocaleString('en-US', { month: 'long' })
  );
  const [detailYear, setDetailYear] = useState<number>(new Date().getFullYear());
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(10);

  // Search filter
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Merchant filter
  const [merchantSearch, setMerchantSearch] = useState('');
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const debouncedMerchantSearch = useDebouncedValue(merchantSearch);

  // Category filter
  const [selectedCategoryId, setSelectedCategoryId] = useState(ALL_VALUE);

  // Date range filter
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  // Sorting state
  const [sortBy, setSortBy] = useState<'merchantName' | 'sales' | 'amount'>('merchantName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Sync text search with debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Fetching categories for dropdown
  const { data: categories } = useMerchantCategories();

  // Fetching merchants for searchable select
  const { data: merchantsData, isLoading: isLoadingMerchants } = useMerchants({
    search: debouncedMerchantSearch || undefined,
    take: 50,
  });

  const merchantOptions = (merchantsData?.data ?? []).map((m) => ({
    value: String(m.id),
    label: m.name,
  }));

  // Format date range strings for API format yyyy-mm-dd
  const fromDateStr = dateRange.from ? dateRange.from.toISOString().split('T')[0] : undefined;
  const toDateStr = dateRange.to ? dateRange.to.toISOString().split('T')[0] : undefined;

  // Querying main coupon sales data
  const { data, isLoading, isFetching, refetch } = useCouponSales({
    skip: page * pageSize,
    take: pageSize,
    search: search || undefined,
    merchantId: selectedMerchantId ? Number(selectedMerchantId) : undefined,
    industryCategoryId:
      selectedCategoryId !== ALL_VALUE && selectedCategoryId
        ? Number(selectedCategoryId)
        : undefined,
    fromDate: fromDateStr,
    toDate: toDateStr,
    sortBy: sortBy,
    sortOrder: sortOrder,
  });

  const salesData = data?.data ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Handler to clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setSearch('');
    setMerchantSearch('');
    setSelectedMerchantId('');
    setSelectedCategoryId(ALL_VALUE);
    setDateRange({ from: undefined, to: undefined });
    setSortBy('merchantName');
    setSortOrder('asc');
    setPage(0);
  };

  // Check if any filters are active
  const hasActiveFilters =
    searchInput !== '' ||
    selectedMerchantId !== '' ||
    selectedCategoryId !== ALL_VALUE ||
    dateRange.from !== undefined ||
    dateRange.to !== undefined;

  // Sorting toggle helper
  const handleSort = (field: 'merchantName' | 'sales' | 'amount') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Render sorting indicators
  const renderSortIcon = (field: 'merchantName' | 'sales' | 'amount') => {
    if (sortBy !== field) {
      return (
        <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground" />
      );
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4 text-primary" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 text-primary" />
    );
  };

  const handleSelectMerchant = (option: { value: string; label: string }) => {
    setMerchantSearch(option.label);
    setSelectedMerchantId(option.value);
    setPage(0);
  };

  const handleMerchantSearchChange = (value: string) => {
    setMerchantSearch(value);
    if (!value) {
      setSelectedMerchantId('');
      setPage(0);
    }
  };

  // Month & Year constants for detail filters
  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Monthly Detail Query
  const {
    data: detailData,
    isLoading: isDetailLoading,
    isFetching: isDetailFetching,
    refetch: refetchDetail,
  } = useMerchantMonthlySales(viewingMerchant ? viewingMerchant.id : 0, {
    skip: detailPage * detailPageSize,
    take: detailPageSize,
    month: detailMonth,
    year: detailYear,
  });

  const detailSales = detailData?.data ?? [];
  const detailTotal = detailData?.count ?? 0;
  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));

  if (viewingMerchant) {
    return (
      <div className="space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewingMerchant(null)}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sales Report
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">{viewingMerchant.name}</h2>
              <p className="text-muted-foreground">Monthly sales and redemption detail report.</p>
            </div>
            {/* <Button
              variant="outline"
              size="icon"
              onClick={() => refetchDetail()}
              disabled={isDetailFetching}
              title="Refresh transaction details"
            >
              <RefreshCw className={`h-4 w-4 ${isDetailFetching ? 'animate-spin' : ''}`} />
            </Button> */}
          </div>
        </div>

        {/* Filters Bar for Details */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-[180px]">
            <Select
              value={detailMonth}
              onValueChange={(val) => {
                setDetailMonth(val);
                setDetailPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[120px]">
            <Select
              value={String(detailYear)}
              onValueChange={(val) => {
                setDetailYear(Number(val));
                setDetailPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Redemptions</CardTitle>
              {/* <Calendar className="h-4 w-4 text-muted-foreground" /> */}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{detailTotal}</div>
              <p className="text-xs text-muted-foreground">
                Total redemptions in {detailMonth} {detailYear}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sales Amount</CardTitle>
              {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ₹{(detailData?.totalSalesAmount ?? 0).toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-muted-foreground">
                Total sales in {detailMonth} {detailYear}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Details Table */}
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-muted-foreground font-medium select-none">
                  <th className="p-4 font-medium">Invoice No.</th>
                  <th className="p-4 font-medium">Redeemed At</th>
                  <th className="p-4 font-medium">Category</th>
                  <th className="p-4 font-medium">Coupon Discount</th>
                  <th className="p-4 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {isDetailLoading && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                        <span>Loading transaction details...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!isDetailLoading && detailSales.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-12 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <FileSpreadsheet className="h-10 w-10 text-muted-foreground/40" />
                        <span className="font-semibold text-foreground">No transactions found</span>
                        <p className="text-xs max-w-xs mx-auto">
                          There are no coupon sales records for this vendor in {detailMonth}{' '}
                          {detailYear}.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                {!isDetailLoading &&
                  detailSales.map((item, index) => {
                    const discountLabel =
                      item.coupon.discountType === 'Percentage'
                        ? `${item.coupon.discountValue}% OFF`
                        : `₹${item.coupon.discountValue} OFF`;

                    return (
                      <tr
                        key={index}
                        className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-4 font-mono font-medium text-primary">{item.invoice}</td>
                        <td className="p-4">
                          {new Date(item.redeemedAt).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:text-blue-400">
                            {item.redeemedMerchant?.industryCategory?.name ?? 'N/A'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center rounded-full bg-pink-100 dark:bg-pink-900/30 px-2.5 py-0.5 text-xs font-semibold text-pink-800 dark:text-pink-400">
                            {discountLabel}
                          </span>
                        </td>
                        <td className="p-4 font-bold text-right text-foreground">
                          ₹{item.amount.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination for Details */}
          {detailTotal > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 border-t p-4 sm:flex-row">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Rows per page</span>
                <Select
                  value={String(detailPageSize)}
                  onValueChange={(value) => {
                    setDetailPageSize(Number(value));
                    setDetailPage(0);
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>
                  Page {detailPage + 1} of {detailTotalPages} ({detailTotal} total)
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={detailPage === 0 || isDetailFetching}
                    onClick={() => setDetailPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={detailPage + 1 >= detailTotalPages || isDetailFetching}
                    onClick={() => setDetailPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Merchant Sales</h2>
          <p className="text-muted-foreground">
            Track and monitor coupon sales and redemptions by merchant partners.
          </p>
        </div>
        {/* <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh Data"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={handleClearFilters} className="text-xs">
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div> */}
      </div>

      {/* Filters Bar */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search merchants..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Merchant Searchable Select */}
        <div>
          <SearchableSelect
            searchTerm={merchantSearch}
            onSearchTermChange={handleMerchantSearchChange}
            options={merchantOptions}
            onSelect={handleSelectMerchant}
            placeholder="Select Merchant..."
            isLoading={isLoadingMerchants}
          />
        </div>

        {/* Industry Category Dropdown */}
        <Select
          value={selectedCategoryId}
          onValueChange={(value) => {
            setSelectedCategoryId(value);
            setPage(0);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All Categories</SelectItem>
            {categories?.map((category) => (
              <SelectItem key={category.id} value={String(category.id)}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range Picker */}
        <div className="lg:col-span-2">
          <DateRangePicker
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              setPage(0);
            }}
            placeholder="Filter by Date Range"
            className="w-full"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-muted-foreground font-medium select-none">
                <th
                  onClick={() => handleSort('merchantName')}
                  className="group cursor-pointer p-4 font-medium transition-colors hover:text-foreground"
                >
                  <div className="flex items-center">
                    Vendor Name
                    {renderSortIcon('merchantName')}
                  </div>
                </th>
                <th className="p-4 font-medium">Month</th>
                <th
                  onClick={() => handleSort('sales')}
                  className="group cursor-pointer p-4 font-medium transition-colors hover:text-foreground"
                >
                  <div className="flex items-center">
                    Redemption Count
                    {renderSortIcon('sales')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('amount')}
                  className="group cursor-pointer p-4 font-medium transition-colors hover:text-foreground"
                >
                  <div className="flex items-center">
                    Total Sales Amount
                    {renderSortIcon('amount')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                      <span>Loading sales report...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && salesData.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Store className="h-10 w-10 text-muted-foreground/40" />
                      <span className="font-semibold text-foreground">No sales data found</span>
                      <p className="text-xs max-w-xs mx-auto">
                        Try adjusting your filters or checking a different date range.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading &&
                salesData.map((item) => (
                  <tr
                    key={`${item.merchantId}-${item.month}`}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() =>
                      setViewingMerchant({ id: item.merchantId, name: item.merchantName })
                    }
                  >
                    <td className="p-4 font-medium flex items-center gap-2">
                      <Store className="h-4 w-4 text-muted-foreground" />
                      {item.merchantName}
                    </td>
                    <td className="p-4">{item.month}</td>
                    <td className="p-4 font-semibold">{item.redemptionCount}</td>
                    <td className="p-4 font-bold text-foreground">
                      ₹{item.totalSalesAmount.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t p-4 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50, 100].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Page {total === 0 ? 0 : page + 1} of {totalPages} ({total} total)
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page + 1 >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
